<template>
  <!--
    AHA2-171 — the single persistent import-progress notification. Behaviour from
    Trang's "Import Flow — Functional Spec"; visuals from her interactive mockup
    (import_flow_all_states.html). ONE element that updates in place from Start
    through completion/cancel/connection-loss, so the bar animates its width
    smoothly instead of being recreated. Gated behind
    release-AHA2-171-import-loading-redesign; off → renders nothing and the
    legacy ImportStatusToaster runs instead.
  -->
  <transition name="import-notif-fade">
    <div
      v-if="shouldRender"
      class="import-notif"
      :class="toastClass"
      role="status"
      aria-live="polite"
      data-testid="import-progress-notification"
    >
      <div class="import-notif__top">
        <div class="import-notif__filedot">
          <!-- AHA2-171 r11: matches the Import Start modal's file row exactly
               (ImportModalV2.vue → UploadListItem.vue) — same coloured
               file-type icon (PowerPoint/document/image/...) resolved from
               the filename, not a generic per-state stroke colour. -->
          <FileTypeIcon :logo-src="fileLogoSrc" :icon-name="fileIconName" />
        </div>
        <div class="import-notif__texts">
          <div class="import-notif__title" data-testid="import-notif-title">
            {{ title }}
          </div>
          <div class="import-notif__sub">
            <span data-testid="import-notif-subtext">{{ subtext }}</span>
          </div>
        </div>
        <div
          v-if="showClose"
          class="import-notif__close"
          data-testid="import-notif-close"
          role="button"
          tabindex="0"
          :aria-label="$i18nPresenter.t('Dismiss')"
          @click="onClose"
          @keyup.enter="onClose"
        >
          ✕
        </div>
      </div>

      <!-- Progress bar. Hidden on the terminal states (Complete/Canceled). -->
      <div
        v-if="showBar"
        class="import-notif__bar-track"
        data-testid="import-notif-bar"
      >
        <div
          class="import-notif__bar-fill"
          :class="{ 'is-animating': isAnimating, 'is-paused': isConnectionLost }"
          :style="{ width: `${displayPercent}%` }"
          data-testid="import-notif-bar-fill"
        />
      </div>

      <!-- Count / percent + inline Cancel (§3 rows 1-3). -->
      <div
        v-if="showBottomRow"
        class="import-notif__bottom"
      >
        <span class="import-notif__count" data-testid="import-notif-count">{{ bottomLeftText }}</span>
        <span
          v-if="showCancel"
          class="import-notif__cancel-link"
          data-testid="import-notif-cancel"
          role="button"
          tabindex="0"
          @click="onCancelClick"
          @keyup.enter="onCancelClick"
        >{{ $i18nPresenter.t('Cancel') }}</span>
      </div>

      <!-- Inline cancel confirmation (§4), n > 0 only. Import keeps running
           underneath; the {n} in the copy stays live. -->
      <div
        v-if="confirmOpen"
        class="import-notif__confirm"
        data-testid="import-notif-confirm"
      >
        <span class="import-notif__confirm-text">{{ cancelConfirmText }}</span>
        <div class="import-notif__confirm-btns">
          <aha-antd-button
            variant="primary"
            class="import-notif__confirm-btn"
            data-testid="import-notif-keep-waiting"
            @click="onKeepWaiting"
          >
            {{ $i18nPresenter.t('Keep waiting') }}
          </aha-antd-button>
          <aha-antd-button
            variant="secondary"
            class="import-notif__confirm-btn"
            data-testid="import-notif-confirm-cancel"
            @click="onConfirmCancel"
          >
            {{ $i18nPresenter.t('Cancel import') }}
          </aha-antd-button>
        </div>
      </div>

      <!-- Connection-lost actions (§5): Try again / Cancel import. -->
      <div
        v-else-if="isConnectionLost"
        class="import-notif__confirm"
        data-testid="import-notif-connection-actions"
      >
        <span
          v-if="count > 0"
          class="import-notif__confirm-text"
          data-testid="import-notif-connection-count"
        >{{ connectionCountText }}</span>
        <div class="import-notif__confirm-btns">
          <aha-antd-button
            variant="primary"
            class="import-notif__confirm-btn"
            data-testid="import-notif-try-again"
            @click="onTryAgain"
          >
            {{ $i18nPresenter.t('Try again') }}
          </aha-antd-button>
          <aha-antd-button
            variant="secondary"
            class="import-notif__confirm-btn"
            data-testid="import-notif-cancel-import"
            @click="onConfirmCancel"
          >
            {{ $i18nPresenter.t('Cancel import') }}
          </aha-antd-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { IMPORT_OPTIONS } from '@/constant/eventActions/ImportEventActions'
import FileTypeIcon from '@/components-v2/upload/FileTypeIcon.vue'
import { getFileTypeIconName, getFileTypeLogo } from '@/utils/fileTypeIcon.utils'
import {
  IMPORT_UI_STATE,
  resolveImportUiState,
  targetPercentForState,
  statusTextForState,
  countTextForState,
  cancelNeedsConfirmation,
  interpolate,
  resolveDisplayTotal,
} from '@/utils/importProgress.utils'

// How high the time-based upload ramp is allowed to crawl before real
// converting progress takes over. Stops just short of the 25% band start so the
// bar never jumps BACKWARDS when the first real count arrives.
const UPLOAD_RAMP_CEILING = 24
const UPLOAD_RAMP_TICK_MS = 400
const UPLOAD_RAMP_EASING = 0.08

// AHA2-171 r12: the toast auto-dismisses this long after a SUCCESSFUL import —
// canceled and connection-lost stay put for the user to act on.
const COMPLETE_AUTO_DISMISS_MS = 2000

export default {
  name: 'ImportProgressNotification',

  components: { FileTypeIcon },

  data() {
    return {
      // Sticky copies so the terminal (Complete/Canceled) card can keep showing
      // after the live import state is torn down.
      capturedFilename: '',
      capturedCount: 0,
      capturedTotal: 0,
      // AHA2-171 r16: sticky captures for the caption fraction, same pattern
      // as capturedCount/capturedTotal — the bar's real driving signal during
      // captioning, so it must survive the terminal poll tick where the store
      // briefly clears importProgress.
      capturedCaptionsDone: 0,
      capturedCaptionsFailed: 0,
      capturedCaptionsTotal: 0,
      // null | 'complete' | 'canceled' — set when an active import ends.
      terminalState: null,
      dismissed: false,
      confirmOpen: false,
      // Set the moment the user commits to cancelling, so the end-of-import
      // watcher resolves to Canceled rather than Complete regardless of how the
      // module's deck-scoped cancel flag is timed.
      cancelCommitted: false,
      displayPercent: 0,
      uploadRampTimer: null,
      // AHA2-171 r12: handle for the 2s auto-dismiss timer started when the
      // import completes successfully (canceled/connection-lost stay put).
      completeDismissTimer: null,
    }
  },

  computed: {
    ...mapGetters('aiChatbotImportPptxPdf', [
      'canUseImportLoadingRedesign',
      'isCurrentPresentationImportingSlides',
      'importProgress',
      'aiChatbotUploadedFile',
      'selectedFileName',
      'selectedImportOption',
      'isCurrentImportConnectionLost',
      'isCurrentImportFailed',
    ]),

    isImportOnly() {
      return this.selectedImportOption === IMPORT_OPTIONS.IMPORT_ONLY
    },

    // AHA-47757: the editable (PPTX → content-v2) import reports through the
    // same importing flag and importProgress shape, so this notification owns
    // its UX under the redesign flag exactly like Import only.
    isEditableImport() {
      return this.selectedImportOption === IMPORT_OPTIONS.IMPORT_EDITABLE
    },

    // An import is genuinely running on the deck on screen.
    isImporting() {
      return this.isCurrentPresentationImportingSlides
    },

    liveFilename() {
      return (
        this.aiChatbotUploadedFile?.file_metadata?.original_filename ||
        this.selectedFileName ||
        ''
      )
    },

    filename() {
      return this.liveFilename || this.capturedFilename
    },

    count() {
      const live = this.importProgress?.slides?.created
      return Number.isFinite(live) ? live : this.capturedCount
    },

    total() {
      // AHA2-171 r11: the backend job's `result.slides` has no `total` at
      // all during the plain "importing" phase — the real total (once known)
      // only shows up as `result.captions.total` once captioning starts (see
      // the sample payload in the ticket). Fall back to that so the UI shows
      // the real denominator instead of reading undefined as 0.
      const live = this.importProgress?.slides?.total ?? this.importProgress?.captions?.total
      return Number.isFinite(live) && live > 0 ? live : this.capturedTotal
    },

    // AHA2-171 r16: the real driving signal for the bar during captioning —
    // see importProgress.utils.js computeCaptionFraction. Sticky-captured
    // like count/total so the value survives the terminal poll tick where
    // importProgress briefly clears.
    captionsDone() {
      const live = this.importProgress?.captions?.done
      return Number.isFinite(live) ? live : this.capturedCaptionsDone
    },

    captionsFailed() {
      const live = this.importProgress?.captions?.failed
      return Number.isFinite(live) ? live : this.capturedCaptionsFailed
    },

    captionsTotal() {
      const live = this.importProgress?.captions?.total
      return Number.isFinite(live) && live > 0 ? live : this.capturedCaptionsTotal
    },

    phase() {
      return this.importProgress?.phase || null
    },

    isConnectionLost() {
      // Only meaningful while an import is actually in flight.
      return this.isImporting && this.isCurrentImportConnectionLost && !this.terminalState
    },

    // AHA2-171 r20: the module flags the deck's import as failed (job 'failed'
    // or waitForImportJob threw/timed out) BEFORE it clears the importing flag,
    // so onImportEnded can read this at end-time and resolve to ERROR rather
    // than faking an "Import complete".
    importFailed() {
      return this.isCurrentImportFailed
    },

    // The resolved spec state (§3), terminal-aware.
    uiState() {
      return resolveImportUiState({
        complete: this.terminalState === 'complete',
        canceled: this.terminalState === 'canceled',
        failed: this.terminalState === 'error',
        connectionLost: this.isConnectionLost,
        isUploading: false,
        phase: this.phase,
        n: this.count,
        total: this.total,
        captionsDone: this.captionsDone,
        captionsFailed: this.captionsFailed,
        captionsTotal: this.captionsTotal,
      })
    },

    isActive() {
      return (
        this.uiState === IMPORT_UI_STATE.UPLOADING ||
        this.uiState === IMPORT_UI_STATE.CONVERTING ||
        this.uiState === IMPORT_UI_STATE.ALMOST_DONE
      )
    },

    shouldRender() {
      if (!this.canUseImportLoadingRedesign) return false
      if (!this.isImportOnly && !this.isEditableImport) return false
      if (this.dismissed) return false
      return this.isImporting || this.isConnectionLost || this.terminalState != null
    },

    toastClass() {
      return {
        'import-notif--success': this.uiState === IMPORT_UI_STATE.COMPLETE,
        'import-notif--canceled': this.uiState === IMPORT_UI_STATE.CANCELED,
        'import-notif--offline': this.uiState === IMPORT_UI_STATE.CONNECTION_LOST,
        'import-notif--error': this.uiState === IMPORT_UI_STATE.ERROR,
      }
    },

    // AHA2-171 r11: same resolver + rendering as the Import Start modal
    // (ImportModalV2 → UploadListItem) — filename alone is enough to resolve
    // PowerPoint/document/image/googleslide; we don't have a mime type here.
    fileIconName() {
      return getFileTypeIconName({ filename: this.filename })
    },
    fileLogoSrc() {
      return getFileTypeLogo({ filename: this.filename }) || ''
    },

    // §3: title is the file name for the live + complete states, and a status
    // headline for canceled / connection-lost.
    title() {
      if (this.uiState === IMPORT_UI_STATE.CANCELED) {
        return this.$i18nPresenter.t('Import canceled')
      }
      if (this.uiState === IMPORT_UI_STATE.CONNECTION_LOST) {
        return this.$i18nPresenter.t('Connection lost')
      }
      if (this.uiState === IMPORT_UI_STATE.ERROR) {
        return this.$i18nPresenter.t('Import failed')
      }
      return this.filename || this.$i18nPresenter.t('Importing slides…')
    },

    // The status line under the filename.
    subtext() {
      if (this.uiState === IMPORT_UI_STATE.CANCELED) {
        // Canceled folds its count into the sub line (title is the headline).
        const { key, args } = countTextForState(this.uiState, { n: this.count, total: this.total })
        return this.translated(key, args)
      }
      // AHA2-171 r18: statusTextForState no longer takes caption args — the
      // status line stays the plain per-state label ("Importing slides…" /
      // "Almost done…") through the whole convert/import/caption span; the
      // numeric readout during captioning now lives in bottomLeftText below.
      const { key, args } = statusTextForState(this.uiState)
      return this.translated(key, args)
    },

    // Bottom-left: percent while uploading (no count yet), otherwise the count.
    bottomLeftText() {
      if (this.uiState === IMPORT_UI_STATE.UPLOADING) {
        return `${Math.round(this.displayPercent)}%`
      }
      // AHA2-171 r18: before captioning has a total, countTextForState
      // returns null and this line is empty — no numeric count line during
      // upload/convert/import (reporter: showing a number here was
      // confusing). Once captioning starts, this reads "{count}/{total}
      // slides imported" off the settled captions.done+failed/total — the
      // SAME copy COMPLETE uses, so it climbs continuously into completion.
      const countCopy = countTextForState(this.uiState, {
        n: this.count,
        total: this.total,
        captionsDone: this.captionsDone,
        captionsFailed: this.captionsFailed,
        captionsTotal: this.captionsTotal,
      })
      if (!countCopy) return ''
      return this.translated(countCopy.key, countCopy.args)
    },

    connectionCountText() {
      // AHA2-171 r20 (finding I): connection-lost is a PAUSE, not a success —
      // the old "…were imported successfully." copy read as COMPLETE while the
      // import was only stalled. Use an explicitly non-terminal "so far" line.
      // Same "never render a total that lies" guard (resolveDisplayTotal) as
      // the COMPLETE/CANCELED branches, and the same plural "{n}/{total} slides"
      // shape already used elsewhere (avoids the "1 slides" singular of a bare
      // count key — see finding M).
      const args = { n: this.count, total: resolveDisplayTotal(this.total, this.count) }
      return this.translated('{n}/{total} slides imported so far', args)
    },

    cancelConfirmText() {
      const args = { n: this.count }
      return this.translated(
        'Cancel import? The {n} slides already created will be kept in your deck.',
        args,
      )
    },

    showBar() {
      // Removed on the terminal states (§3 rows 4-5) and on ERROR (r20 — a
      // failed import has no meaningful progress to show).
      return this.uiState !== IMPORT_UI_STATE.COMPLETE &&
        this.uiState !== IMPORT_UI_STATE.CANCELED &&
        this.uiState !== IMPORT_UI_STATE.ERROR
    },

    isAnimating() {
      // Continuous stripe motion whenever actively importing; frozen on
      // connection-loss (§2 / §5).
      return this.isActive && !this.isConnectionLost
    },

    showClose() {
      // Only the terminal states offer the dismiss ✕ (§3) — Complete, Canceled
      // and, since r20, the ERROR card.
      return this.uiState === IMPORT_UI_STATE.COMPLETE ||
        this.uiState === IMPORT_UI_STATE.CANCELED ||
        this.uiState === IMPORT_UI_STATE.ERROR
    },

    showCancel() {
      // Cancel is offered in Uploading and Converting, hidden in Almost done and
      // on the terminal / connection states (those render their own buttons).
      return this.uiState === IMPORT_UI_STATE.UPLOADING ||
        this.uiState === IMPORT_UI_STATE.CONVERTING
    },

    showBottomRow() {
      // The count/percent row shows for every state that has a bottom count and
      // is not driving its own confirm/offline button row.
      if (this.confirmOpen || this.isConnectionLost) return false
      return this.uiState === IMPORT_UI_STATE.UPLOADING ||
        this.uiState === IMPORT_UI_STATE.CONVERTING ||
        this.uiState === IMPORT_UI_STATE.ALMOST_DONE ||
        this.uiState === IMPORT_UI_STATE.COMPLETE
    },
  },

  watch: {
    isImporting(now, was) {
      if (now && !was) {
        this.onImportStarted()
      } else if (!now && was) {
        this.onImportEnded()
      }
    },

    liveFilename(name) {
      if (name) this.capturedFilename = name
    },

    // Keep the animated bar tracking real progress. Monotonic: never step back.
    uiState() {
      this.syncDisplayPercent()
    },
    count() {
      this.captureCounts()
      this.syncDisplayPercent()
    },
    total() {
      this.captureCounts()
      this.syncDisplayPercent()
    },
    // AHA2-171 r16: captionsDone is the bar's primary driving signal for the
    // whole (long) captioning phase — uiState typically stays CONVERTING as
    // it climbs from 0 to captionsTotal, so it needs its own watcher rather
    // than relying on a uiState transition to re-sync the bar.
    captionsDone() {
      this.captureCounts()
      this.syncDisplayPercent()
    },
    captionsFailed() {
      this.captureCounts()
      this.syncDisplayPercent()
    },
    captionsTotal() {
      this.captureCounts()
      this.syncDisplayPercent()
    },

    isConnectionLost(lost) {
      // Freeze the ramp while offline; resume when the connection returns.
      if (lost) {
        this.stopUploadRamp()
      } else if (this.uiState === IMPORT_UI_STATE.UPLOADING) {
        this.startUploadRamp()
      }
    },

    // If completion lands while the confirmation is open, auto-dismiss it (§4).
    // AHA2-171 r12: a SUCCESSFUL completion also auto-dismisses the whole toast
    // after 2s — canceled/connection-lost are left for the user to act on.
    terminalState(state) {
      if (state) this.confirmOpen = false
      this.clearCompleteDismissTimer()
      if (state === 'complete') {
        this.completeDismissTimer = setTimeout(() => {
          this.dismissed = true
        }, COMPLETE_AUTO_DISMISS_MS)
      }
    },
  },

  mounted() {
    // Prompt connection-loss feedback from the browser itself (§5). The
    // resilient poll loop is the backstop for a reachable-but-dead server; this
    // covers the common "wifi dropped" case instantly.
    window.addEventListener('offline', this.onBrowserOffline)
    window.addEventListener('online', this.onBrowserOnline)
    if (this.isImporting) this.onImportStarted()
  },

  beforeDestroy() {
    this.stopUploadRamp()
    this.clearCompleteDismissTimer()
    window.removeEventListener('offline', this.onBrowserOffline)
    window.removeEventListener('online', this.onBrowserOnline)
  },

  methods: {
    ...mapActions('aiChatbotImportPptxPdf', [
      'requestImportCancelAction',
      'clearImportCancelRequestAction',
      'setImportConnectionLostAction',
      'setImportFailedAction',
    ]),

    // Clear the deck-scoped failed flag so a dismissed error can't leak into a
    // later import on the same deck. buildRedesignPollHooks also resets it at
    // the start of every fresh run — this is the user-dismiss path.
    clearImportFailed() {
      this.setImportFailedAction({ presentationId: this.currentDeckId(), failed: false })
    },

    // interpolate belt: vue-i18n v8 returns the key literally (no
    // interpolation) until the CDN-loaded translations register this
    // feature's new keys from public/base_language.json. interpolate()
    // substitutes any leftover {name} tokens either way — no-op once the
    // translation lands, fallback before then. See importProgress.utils.js.
    translated(key, args) {
      return args
        ? interpolate(this.$i18nPresenter.t(key, args), args)
        : this.$i18nPresenter.t(key)
    },

    onImportStarted() {
      // A new import cancels any pending auto-dismiss from a previous run and
      // re-shows the toast (r12).
      this.clearCompleteDismissTimer()
      this.terminalState = null
      this.dismissed = false
      this.confirmOpen = false
      this.cancelCommitted = false
      this.capturedFilename = this.liveFilename
      this.capturedCount = 0
      this.capturedTotal = 0
      this.capturedCaptionsDone = 0
      this.capturedCaptionsFailed = 0
      this.capturedCaptionsTotal = 0
      this.displayPercent = 0
      this.syncDisplayPercent()
    },

    onImportEnded() {
      this.captureCounts()
      // Priority: an explicit cancel wins (user intent); then a genuine failure
      // /timeout (r20 — the module flags importFailed before it clears the
      // importing flag, so it's readable here); then a real completion; then
      // nothing-created is dismissed silently. Failure OUTRANKS the "≥1 slide
      // created" completion check, so a partial import that then failed shows
      // the error card instead of an "Import complete".
      if (this.cancelCommitted) {
        this.terminalState = 'canceled'
      } else if (this.importFailed) {
        this.terminalState = 'error'
      } else if (this.capturedTotal > 0 || this.capturedCount > 0) {
        this.terminalState = 'complete'
        this.displayPercent = 100
      } else {
        this.dismissed = true
      }
      this.stopUploadRamp()
    },

    captureCounts() {
      const c = this.importProgress?.slides?.created
      // Same slides.total ?? captions.total fallback as the `total` computed
      // above — keep the sticky captured total in sync with whichever field
      // the backend actually populated.
      const t = this.importProgress?.slides?.total ?? this.importProgress?.captions?.total
      if (Number.isFinite(c)) this.capturedCount = c
      if (Number.isFinite(t) && t > 0) this.capturedTotal = t

      // AHA2-171 r16: sticky-capture the caption counts too, same guard shape.
      const capDone = this.importProgress?.captions?.done
      const capFailed = this.importProgress?.captions?.failed
      const capTotal = this.importProgress?.captions?.total
      if (Number.isFinite(capDone)) this.capturedCaptionsDone = capDone
      if (Number.isFinite(capFailed)) this.capturedCaptionsFailed = capFailed
      if (Number.isFinite(capTotal) && capTotal > 0) this.capturedCaptionsTotal = capTotal
    },

    syncDisplayPercent() {
      if (this.isConnectionLost) return // frozen
      if (this.uiState === IMPORT_UI_STATE.UPLOADING) {
        // AHA2-171 r15: a spurious late re-entry into UPLOADING (the store
        // briefly clears importProgress on the terminal poll tick, so
        // resolveImportUiState reports UPLOADING for one tick before
        // terminalState flips to 'complete') must not restart the ramp once
        // displayPercent is already past the ramp's ceiling — otherwise the
        // ramp's outer clamp yanks the bar backward to the ceiling. Only ramp
        // while genuinely below it; otherwise freeze in place and let the
        // real terminal state (below) or 'complete' handler take it from here.
        if (this.displayPercent < UPLOAD_RAMP_CEILING) {
          this.startUploadRamp()
        } else {
          this.stopUploadRamp()
        }
        return
      }
      this.stopUploadRamp()
      const target = targetPercentForState(this.uiState, {
        n: this.count,
        total: this.total,
        captionsDone: this.captionsDone,
        captionsFailed: this.captionsFailed,
        captionsTotal: this.captionsTotal,
      })
      if (target != null) {
        // Monotonic — a smooth CSS width transition animates old→new.
        this.displayPercent = Math.max(this.displayPercent, target)
      }
    },

    startUploadRamp() {
      if (this.uploadRampTimer) return
      this.uploadRampTimer = setInterval(() => {
        // Asymptotic crawl toward the ceiling so the bar always looks like it's
        // working even though upload reports no real percentage.
        const next = this.displayPercent + (UPLOAD_RAMP_CEILING - this.displayPercent) * UPLOAD_RAMP_EASING
        // AHA2-171 r15: strictly monotonic. Clamp the CANDIDATE to the
        // ceiling first, then max against the current value — so if
        // displayPercent is already above the ceiling (a late spurious
        // ramp-tick near completion) it is never pulled back down to 24.
        this.displayPercent = Math.max(this.displayPercent, Math.min(next, UPLOAD_RAMP_CEILING))
      }, UPLOAD_RAMP_TICK_MS)
    },

    stopUploadRamp() {
      if (this.uploadRampTimer) {
        clearInterval(this.uploadRampTimer)
        this.uploadRampTimer = null
      }
    },

    onCancelClick() {
      // §4: confirmation vs. instant is decided by n, not by the named state.
      if (cancelNeedsConfirmation(this.count)) {
        this.confirmOpen = true
      } else {
        this.commitCancel()
      }
    },

    onKeepWaiting() {
      // Import never paused; just close the confirmation.
      this.confirmOpen = false
    },

    onConfirmCancel() {
      this.confirmOpen = false
      this.commitCancel()
    },

    commitCancel() {
      this.cancelCommitted = true
      this.requestImportCancelAction()
    },

    onTryAgain() {
      // Optimistically clear the warning state; the resilient poll loop is already
      // retrying, so the next successful tick confirms (or re-flags) it.
      this.setImportConnectionLostAction({ presentationId: null, lost: false })
    },

    currentDeckId() {
      return this.$store?.state?.presentation?.id ?? null
    },

    onBrowserOffline() {
      if (!this.isImporting) return
      this.setImportConnectionLostAction({ presentationId: this.currentDeckId(), lost: true })
    },

    onBrowserOnline() {
      // Auto-recovery (§5): clear the warning state; the poll loop resumes on its
      // own from exactly where it stalled — nothing already converted is lost.
      this.setImportConnectionLostAction({ presentationId: this.currentDeckId(), lost: false })
    },

    onClose() {
      this.clearCompleteDismissTimer()
      this.dismissed = true
      this.terminalState = null
      this.clearImportCancelRequestAction()
      this.clearImportFailed()
    },

    clearCompleteDismissTimer() {
      if (this.completeDismissTimer) {
        clearTimeout(this.completeDismissTimer)
        this.completeDismissTimer = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Palette from Trang's mockup (import_flow_all_states.html), remapped to the October 2026 colour rules.
$purple: #e70e68;
$purple-dark: #db005b;
$purple-light: #fef3f7;
$pink: #e70e68;
$ink: #1a1a1a;
$ink-soft: #6b6478;
$line: #fef3f7;
$success-ink: #1caa6a;
$success-surface: #e3f8ee;
$warning-ink: #e0a52e;

.import-notif {
  position: fixed;
  right: 24px;
  // Clear of the bottom-right support-chat launcher (§2).
  bottom: 88px;
  z-index: 1100;
  width: 320px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 45px -12px rgba(26, 26, 26, 0.35), 0 0 0 1px $line;
  padding: 14px 16px 16px;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: box-shadow 0.35s ease;
}

.import-notif__top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.import-notif__filedot {
  // AHA2-171 r11: hosts the coloured file-type icon (matches the Import
  // Start modal) instead of a per-state stroke-colour glyph — no background
  // square needed, the icon itself carries its real colours.
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.import-notif__texts {
  min-width: 0;
  flex: 1;
}

.import-notif__title {
  font-size: 12.5px;
  font-weight: 700;
  color: $ink;
  line-height: 1.3;
  word-break: break-word;
}

.import-notif__sub {
  font-size: 11px;
  color: $ink-soft;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.import-notif__close {
  margin-left: auto;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $ink-soft;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover { background: rgba(26, 26, 26, 0.06); }
}

.import-notif__bar-track {
  width: 100%;
  height: 5px;
  border-radius: 99px;
  background: rgba(231, 14, 104, 0.12);
  overflow: hidden;
  transition: opacity 0.3s ease;
}

.import-notif__bar-fill {
  height: 100%;
  border-radius: 99px;
  background: $purple;
  // Ease the width old→new on every update (§2).
  transition: width 1.7s cubic-bezier(0.2, 0.65, 0.3, 1);
  position: relative;
  overflow: hidden;

  &.is-paused {
    background: #d9d4e2;
  }
}

// Continuous moving-stripe motion while actively importing (§2). Only Connection
// lost lacks is-animating, so its bar is genuinely still.
.import-notif__bar-fill.is-animating::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.32) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.32) 50%,
    rgba(255, 255, 255, 0.32) 75%,
    transparent 75%,
    transparent
  );
  background-size: 20px 20px;
  animation: import-notif-stripes 0.9s linear infinite;
}

.import-notif__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 9px;
}

.import-notif__count {
  font-size: 11px;
  font-weight: 400;
  color: $purple-dark;

  .import-notif--success & { color: #0f7a4a; }
}

// AHA2-171 r20: the failure card. A red hairline + red headline so a failed
// import is unmistakably not the green "Import complete".
$red: #d5493f;
$red-line: #f2b8b5;

.import-notif--error {
  box-shadow: 0 20px 45px -12px rgba(26, 26, 26, 0.35), 0 0 0 1px $red-line;

  .import-notif__title { color: $red; }
}

.import-notif__cancel-link {
  font-size: 11px;
  font-weight: 700;
  color: $ink-soft;
  cursor: pointer;

  &:hover { color: $pink; }
}

.import-notif__confirm {
  margin-top: 11px;
  padding-top: 11px;
  animation: import-notif-fadein 0.2s ease both;
}

.import-notif__confirm-text {
  font-size: 11px;
  color: $ink-soft;
  line-height: 1.5;
  display: block;
  margin-bottom: 9px;
}

.import-notif__confirm-btns {
  display: flex;
  gap: 8px;
}

.import-notif__confirm-btn {
  flex: 1;
}

@keyframes import-notif-stripes {
  from { background-position: 0 0; }
  to { background-position: 20px 0; }
}

@keyframes import-notif-fadein {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.import-notif-fade-enter-active,
.import-notif-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.import-notif-fade-enter,
.import-notif-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
